import * as React from 'react';

import { TCollapsibleProps } from './types';

const CollapsibleContext = React.createContext<{
  open: boolean;
  toggle: () => void;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
}>({ open: false, toggle: () => {} });

const Collapsible = ({ open = false, onOpenChange, children }: TCollapsibleProps) => {
  const [internalOpen, setInternalOpen] = React.useState(open);

  React.useEffect(() => {
    setInternalOpen(open);
  }, [open]);

  const toggle = () => {
    const newOpen = !internalOpen;
    setInternalOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  return (
    <CollapsibleContext.Provider value={{ open: internalOpen, toggle }}>
      {children}
    </CollapsibleContext.Provider>
  );
};

interface CollapsibleTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

const CollapsibleTrigger = React.forwardRef<HTMLButtonElement, CollapsibleTriggerProps>(
  ({ children, className, ...props }, ref) => {
    const { toggle } = React.useContext(CollapsibleContext);

    return (
      <button ref={ref} onClick={toggle} className={className} {...props}>
        {children}
      </button>
    );
  },
);
CollapsibleTrigger.displayName = 'CollapsibleTrigger';

interface CollapsibleContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const CollapsibleContent = React.forwardRef<HTMLDivElement, CollapsibleContentProps>(
  ({ children, className = '', ...props }, ref) => {
    const { open } = React.useContext(CollapsibleContext);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [height, setHeight] = React.useState<number | undefined>(open ? undefined : 0);
    const [shouldRender, setShouldRender] = React.useState(open);

    React.useEffect(() => {
      let rafId: number;
      let timerId: ReturnType<typeof setTimeout>;

      if (open) {
        setShouldRender(true);
        rafId = requestAnimationFrame(() => {
          if (contentRef.current) {
            setHeight(contentRef.current.scrollHeight);
            timerId = setTimeout(() => setHeight(undefined), 300);
          }
        });
      } else {
        if (shouldRender && contentRef.current) {
          const currentHeight = contentRef.current.scrollHeight;
          setHeight(currentHeight);
          rafId = requestAnimationFrame(() => {
            setHeight(0);
            timerId = setTimeout(() => setShouldRender(false), 300);
          });
        } else {
          setShouldRender(false);
        }
      }

      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timerId);
      };
    }, [open]);

    // Don't render anything if we shouldn't render
    if (!shouldRender) return null;

    return (
      <div className={`relative ${className}`} ref={ref} {...props}>
        {/* Content wrapper with height animation */}
        <div
          style={{
            height: height !== undefined ? `${height}px` : 'auto',
            overflow: 'hidden',
            transition: 'height 300ms ease-in-out',
          }}
        >
          <div ref={contentRef}>{children}</div>
        </div>
      </div>
    );
  },
);

CollapsibleContent.displayName = 'CollapsibleContent';

export { Collapsible, CollapsibleContent, CollapsibleTrigger };
